import { useEffect } from 'react'

import { Heading } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { FullScreen } from '@modals/layout'
import { custodyCircleWalletUSDC } from '@services/sections/lib/custody'
import { routes } from '@services/sections/modal/routes'
import { useSegment } from '@services/segment'
import { CHAINED_TRANSFER_APPROVAL_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Center } from '@shared/components/layout'
import {
  useWalletApprovalTransactionDetailQuery,
  WalletApprovalTransactionStatus,
} from '@shared/graphql'
import Link from 'next/link'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

export interface PendingApprovalProps {
  /**
   * Wallet approval proposal ID.
   */
  proposalId?: string
}

export const PendingApproval: React.FC<PendingApprovalProps> = ({
  proposalId,
}) => {
  const { t } = useTranslation('ace')
  const { router } = useModal()
  const { trackLinkClick } = useSegment()

  const { data, error, refetch } = useWalletApprovalTransactionDetailQuery({
    variables: {
      proposalId: proposalId ?? '',
    },
    skip: !proposalId,
    pollInterval: CHAINED_TRANSFER_APPROVAL_POLL_INTERVAL,
  })

  useEffect(() => {
    const result = data?.walletApprovalTransactionDetail

    // If successful, push to next route to continue
    if (result?.status === WalletApprovalTransactionStatus.COMPLETED) {
      return router.push({
        pathname: routes.blockchain.aceSendOnchain.send,
        query: {
          id: result.transferId,
        },
      })
    }

    // If any failure state, push to failure route
    if (
      result?.status != null &&
      [
        WalletApprovalTransactionStatus.CANCELLED_BY_INITIATOR,
        WalletApprovalTransactionStatus.EXPIRED,
        WalletApprovalTransactionStatus.FAILED,
        WalletApprovalTransactionStatus.REJECTED_BY_USER,
      ].includes(result.status)
    ) {
      return router.push(routes.blockchain.aceSendOnchain.failure)
    }

    // Else, it's still pending, do nothing and continue polling
  }, [data?.walletApprovalTransactionDetail, router])

  return (
    <FullScreen totalSteps={2}>
      <Center>
        <GraphQLErrorBoundary error={error} retry={refetch}>
          <Heading
            alignment="center"
            iconName="CircleSpinnerSolid"
            layout="vertical"
            variant="page/sm"
            loop
          >
            <Heading.Header as="h2">
              {t(`sendOnchain.pendingApproval.heading`)}
            </Heading.Header>
            <Heading.Intro as="p" className="w-168 text-neutral-subtle">
              <Trans
                components={{
                  a: (
                    <Link
                      className="font-circular-bold"
                      href={custodyCircleWalletUSDC.route}
                      onClick={trackLinkClick}
                    />
                  ),
                }}
                i18nKey="ace:sendOnchain.pendingApproval.subheading"
              />
            </Heading.Intro>
          </Heading>
        </GraphQLErrorBoundary>
      </Center>
    </FullScreen>
  )
}
